// apps/smart-shikka-web/src/surfaces/surfaces.tsx
// PR-CORE-11 — Projects / Files / Automations / Apps / Code / Design / Activity
// surfaces. Each reads only canonical durable endpoints; a denied or absent
// backend renders the truthful unavailable/empty state, never fake data.
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import {
  ActivityLedgerView,
  assistantTurnFromMessage,
  buildActivityLedger,
  productText,
  useBrandColors,
  type LedgerAssistantTurn,
  type LedgerApproval,
  type LedgerCoworkRun,
  type LedgerUsageEvent,
} from "@smart-shikka/react";
import { useLocale } from "../ui.js";
import {
  EmptyState,
  SurfacePage,
  SurfaceStateView,
  surfaceGetJson,
  surfaceApiBase,
  surfaceHeaders,
  useSurfaceQuery,
} from "./surfaceScaffold.js";

function cardStyle(C: ReturnType<typeof useBrandColors>) {
  return {
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "10px 14px",
    background: C.card,
  } as const;
}

function Grid(props: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
      {props.children}
    </div>
  );
}

/* ── Projects ───────────────────────────────────────────────────────────── */

interface ProjectRow {
  projectId: string;
  displayName: string;
  repository: string;
  visibility: string;
  defaultBranch: string;
}

export function ProjectsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  return (
    <SurfacePage
      route="projects"
      title={productText("navProjects", locale)}
      subtitle={locale === "hi" ? "कैनॉनिकल सर्वर प्रोजेक्ट्स" : "Canonical server projects"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((p) => (
              <div key={p.projectId} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{p.displayName}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{p.repository}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                  {p.visibility} · {p.defaultBranch}
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Files ──────────────────────────────────────────────────────────────── */

interface LibraryRow {
  fileId: string;
  path: string;
  size: number;
  language?: string | undefined;
  sourceKind: "artifact" | "project_index";
  artifactSource?: string | undefined;
  extractionStatus: string;
  extractionMessage?: string | undefined;
  chunkCount: number;
  hasAppProvenance: boolean;
  retrievable: boolean;
  stale: boolean;
  lastIndexedAt?: number | undefined;
}

function knowledgeStatusLabel(status: string, locale: string): string {
  const hi = locale === "hi";
  switch (status) {
    case "extracted": return hi ? "निकाला गया" : "extracted";
    case "pending": return hi ? "प्रतीक्षा में" : "pending";
    case "unsupported": return hi ? "असमर्थित" : "unsupported";
    case "error": return hi ? "त्रुटि" : "error";
    default: return status;
  }
}

export function FilesSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const projectsQ = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  const [selected, setSelected] = useState<string>("");
  const projectId =
    selected || (projectsQ.kind === "ready" ? projectsQ.data[0]?.projectId ?? "" : "");
  const filesQ = useSurfaceQuery<LibraryRow[]>(
    projectId ? `/api/projects/${encodeURIComponent(projectId)}/knowledge/library` : null,
    (d) =>
      (Array.isArray(d.entries) ? (d.entries as Record<string, unknown>[]) : []).map((f) => ({
        fileId: String(f.fileId ?? ""),
        path: String(f.path ?? ""),
        size: typeof f.size === "number" ? f.size : 0,
        language: typeof f.language === "string" ? f.language : undefined,
        sourceKind: f.sourceKind === "artifact" ? "artifact" : "project_index",
        artifactSource: typeof f.artifactSource === "string" ? f.artifactSource : undefined,
        extractionStatus: String(f.extractionStatus ?? "pending"),
        extractionMessage: typeof f.extractionMessage === "string" ? f.extractionMessage : undefined,
        chunkCount: typeof f.chunkCount === "number" ? f.chunkCount : 0,
        hasAppProvenance: f.hasAppProvenance === true,
        retrievable: f.retrievable === true,
        stale: f.stale === true,
        lastIndexedAt: typeof f.lastIndexedAt === "number" ? f.lastIndexedAt : undefined,
      })),
  );

  return (
    <SurfacePage
      route="files"
      title={productText("navFiles", locale)}
      subtitle={locale === "hi" ? "Knowledge Library — प्रोजेक्ट sources की सच्ची स्थिति" : "Knowledge Library — truthful source lifecycle"}
    >
      {projectsQ.kind === "ready" && projectsQ.data.length === 0 ? (
        <EmptyState />
      ) : projectsQ.kind !== "ready" ? (
        <SurfaceStateView state={projectsQ} onRetry={projectsQ.retry} />
      ) : (
        <>
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="ss-files-project" style={{ fontSize: 12, color: C.muted }}>
              {productText("navProjects", locale)}:
            </label>
            <select
              id="ss-files-project"
              value={projectId}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                fontSize: 12,
                padding: "4px 8px",
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: C.card,
                color: C.fg,
              }}
            >
              {projectsQ.data.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
          {filesQ.kind === "ready" ? (
            filesQ.data.length === 0 ? (
              <EmptyState />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {filesQ.data.map((f) => (
                  <div
                    key={f.fileId}
                    style={{
                      ...cardStyle(C),
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <span style={{ fontSize: 12, color: C.fg, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {f.path}
                      </span>
                      <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>
                        {f.language ? `${f.language} · ` : ""}
                        {(f.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: C.muted }}>
                      {knowledgeStatusLabel(f.extractionStatus, locale)}
                      {" · "}
                      {f.chunkCount} chunks
                      {" · "}
                      {f.sourceKind === "artifact"
                        ? (locale === "hi" ? "अपलोड artifact" : "uploaded artifact") + (f.artifactSource ? ` (${f.artifactSource})` : "")
                        : (locale === "hi" ? "प्रोजेक्ट index" : "project index")}
                      {f.hasAppProvenance ? " · live-state app source" : ""}
                      {f.stale ? " · stale" : ""}
                    </span>
                    <span style={{ fontSize: 11, color: f.retrievable ? C.fg : C.muted }}>
                      {f.retrievable
                        ? (locale === "hi" ? "assistant context में उपलब्ध" : "available in assistant context")
                        : (locale === "hi" ? "assistant context में उपलब्ध नहीं" : "not in assistant context")}
                      {f.lastIndexedAt ? ` · ${new Date(f.lastIndexedAt).toISOString().slice(0, 10)}` : ""}
                    </span>
                    {(f.extractionStatus === "error" || f.extractionStatus === "unsupported") && f.extractionMessage ? (
                      <span style={{ fontSize: 11, color: C.muted }}>{f.extractionMessage}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )
          ) : (
            <SurfaceStateView state={filesQ} onRetry={filesQ.retry} />
          )}
        </>
      )}
    </SurfacePage>
  );
}

/* ── Automations ────────────────────────────────────────────────────────── */

interface AutomationRow {
  id: string;
  name: string;
  status: string;
  schedule?: string | undefined;
}

export function AutomationsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<AutomationRow[]>("/api/cowork/automations", (d) =>
    (Array.isArray(d.automations) ? (d.automations as Record<string, unknown>[]) : []).map((a) => ({
      id: String(a.automationId ?? a.id ?? ""),
      name: String(a.name ?? a.title ?? a.automationId ?? a.id ?? ""),
      status: String(a.status ?? "active"),
      schedule: typeof a.schedule === "string" ? a.schedule : undefined,
    })),
  );
  return (
    <SurfacePage
      route="automation"
      title={productText("navAutomation", locale)}
      subtitle={locale === "hi" ? "कैनॉनिकल cowork automation" : "Canonical cowork automations"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((a) => (
              <div key={a.id} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{a.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  {a.status}
                  {a.schedule ? ` · ${a.schedule}` : ""}
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Apps ───────────────────────────────────────────────────────────────── */

interface AppRow {
  appId: string;
  displayName: string;
  connectionStatus: string;
  publicApiStatus: string;
  allowed: number;
}

export function AppsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<AppRow[]>("/api/public/apps", (d) =>
    (Array.isArray(d.apps) ? (d.apps as Record<string, unknown>[]) : []).map((a) => ({
      appId: String(a.appId ?? ""),
      displayName: String(a.displayName ?? a.appId ?? ""),
      connectionStatus: String(a.connectionStatus ?? ""),
      publicApiStatus: String(a.publicApiStatus ?? ""),
      allowed: Array.isArray(a.allowedPublicCapabilities) ? a.allowedPublicCapabilities.length : 0,
    })),
  );
  return (
    <SurfacePage
      route="apps"
      title={productText("navApps", locale)}
      subtitle={locale === "hi" ? "सर्वेन app federation — public preview" : "Seven-app federation — public preview"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((a) => (
              <div key={a.appId} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{a.displayName}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{a.appId}</div>
                <div style={{ fontSize: 11, marginTop: 6 }}>
                  <span
                    style={{
                      padding: "1px 8px",
                      borderRadius: 999,
                      border: `1px solid ${C.border}`,
                      color: C.muted,
                    }}
                  >
                    {a.publicApiStatus === "preview_only" ? productText("surfacePreview", locale) : a.publicApiStatus}
                  </span>
                  <span style={{ color: C.muted, marginLeft: 8 }}>
                    {a.allowed} {locale === "hi" ? "कैपेबिलिटी" : "capabilities"}
                  </span>
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Code ───────────────────────────────────────────────────────────────── */

interface CodingTaskRow {
  taskId: string;
  status: string;
  description: string;
}

interface CodingTaskDetail {
  taskId: string;
  state: string;
  branch: string;
  workspaceId: string;
  approvalState: string;
  budgetUsage: string;
  planText: string;
  prUrl: string;
  evidenceSummary: string;
}

interface DesktopBindingRow {
  desktopId: string;
  status: string;
  lastHeartbeatAt: string | null;
}

/*
 * Web→Desktop handoff card. The browser never performs trusted local
 * coding/desktop execution — it only shows the truthful device state and, when
 * a bound Desktop is online, hands off via `smartshikka://handoff/<id>` whose
 * only payload is the opaque one-time id (Desktop resolves the authenticated
 * project/context server-side; no token or secret ever travels in the URL).
 */
function CodeDesktopHandoffCard() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: "launched" | "error"; text: string } | null>(null);
  const desktopQ = useSurfaceQuery<DesktopBindingRow[]>("/api/mobile/desktop/status", (d) =>
    (Array.isArray(d.bindings) ? (d.bindings as Record<string, unknown>[]) : []).map((b) => ({
      desktopId: String(b.desktopId ?? ""),
      status: String(b.status ?? ""),
      lastHeartbeatAt: b.lastHeartbeatAt ? String(b.lastHeartbeatAt) : null,
    })),
  );

  const openInDesktop = () => {
    if (!token || busy) return;
    setBusy(true);
    setNotice(null);
    api
      .mobileDesktopHandoff(token, "code")
      .then((res) => {
        if (res.ok && res.data?.deepLink) {
          setNotice({
            kind: "launched",
            text: locale === "hi"
              ? "Desktop खोला जा रहा है… न खुले तो दोबारा क्लिक करें"
              : "Opening Desktop… click again if it does not launch",
          });
          // The OS hands the scheme to the installed app; nothing else is sent.
          window.location.href = res.data.deepLink;
        } else {
          setNotice({ kind: "error", text: locale === "hi" ? `हैंडऑफ़ विफल: ${res.error ?? "unknown"}` : `Handoff failed: ${res.error ?? "unknown"}` });
        }
      })
      .catch(() => setNotice({ kind: "error", text: locale === "hi" ? "हैंडऑफ़ विफल" : "Handoff failed" }))
      .finally(() => setBusy(false));
  };

  const title = locale === "hi" ? "Coding execution" : "Coding execution";
  const active = desktopQ.kind === "ready" ? desktopQ.data.filter((b) => b.status === "active") : [];
  const inactive = desktopQ.kind === "ready" ? desktopQ.data : [];

  return (
    <div style={{ ...cardStyle(C), marginBottom: 14 }} data-testid="code-desktop-handoff">
      <div style={{ fontSize: 13, fontWeight: 600, color: C.fg, marginBottom: 6 }}>{title}</div>
      {desktopQ.kind === "ready" ? (
        active.length > 0 ? (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
              {locale === "hi"
                ? "Smart Shikka Desktop जुड़ा है — execution authority डेस्कटॉप के पास रहेगी"
                : "Smart Shikka Desktop is connected — it remains the execution authority"}
            </div>
            <button
              type="button"
              onClick={openInDesktop}
              disabled={busy}
              style={{
                padding: "7px 14px",
                borderRadius: 999,
                border: `1px solid ${C.primary}`,
                background: "transparent",
                color: C.primary,
                fontSize: 12,
                fontWeight: 600,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {busy
                ? locale === "hi" ? "खुल रहा है…" : "Opening…"
                : locale === "hi" ? "Smart Shikka Desktop में खोलें" : "Open in Smart Shikka Desktop"}
            </button>
            {notice ? (
              <span role={notice.kind === "error" ? "alert" : "status"} style={{ marginLeft: 10, fontSize: 11, color: notice.kind === "error" ? "#b3261e" : C.muted }}>
                {notice.text}
              </span>
            ) : null}
          </>
        ) : inactive.length > 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>
            <strong style={{ color: C.fg }}>
              {locale === "hi" ? "Reconnect Desktop" : "Reconnect Desktop"}
            </strong>
            {" · "}
            {locale === "hi"
              ? `Desktop जुड़ा नहीं है (${inactive.map((b) => b.status || "offline").join(", ")}${inactive[0]?.lastHeartbeatAt ? ` · अंतिम heartbeat ${inactive[0].lastHeartbeatAt}` : ""}) — coding execution अभी उपलब्ध नहीं`
              : `Desktop is not connected (${inactive.map((b) => b.status || "offline").join(", ")}${inactive[0]?.lastHeartbeatAt ? ` · last heartbeat ${inactive[0].lastHeartbeatAt}` : ""}) — coding execution is not available`}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
              {locale === "hi"
                ? "Coding execution के लिए Smart Shikka Desktop ज़रूरी है — ब्राउज़र स्थानीय coding नहीं चलाता"
                : "Coding execution requires Smart Shikka Desktop — the browser never runs local coding"}
            </div>
            <DesktopDownloadCta />
          </>
        )
      ) : desktopQ.kind === "unavailable" ? (
        <div style={{ fontSize: 12, color: C.muted }}>
          {locale === "hi"
            ? "Desktop स्थिति केवल owner session में दिखती है"
            : "Desktop status is visible to the owner session only"}
        </div>
      ) : (
        <SurfaceStateView state={desktopQ} onRetry={desktopQ.retry} />
      )}
    </div>
  );
}

export function CodeSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const projectsQ = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  const { token } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const projectId =
    selectedProjectId || (projectsQ.kind === "ready" ? projectsQ.data[0]?.projectId ?? "" : "");
  const tasksQ = useSurfaceQuery<CodingTaskRow[]>(
    projectId ? `/api/projects/${encodeURIComponent(projectId)}/coding/tasks` : null,
    (d) =>
      (Array.isArray(d.tasks) ? (d.tasks as Record<string, unknown>[]) : []).map((t) => ({
        taskId: String(t.taskId ?? t.id ?? ""),
        status: String(t.state ?? t.status ?? ""),
        description: String(t.description ?? t.title ?? ""),
      })),
  );
  const [openTaskId, setOpenTaskId] = useState("");
  const [detail, setDetail] = useState<CodingTaskDetail | null>(null);
  const [detailTaskId, setDetailTaskId] = useState("");
  const [detailError, setDetailError] = useState("");
  const detailReqRef = useRef(0);
  const [createDesc, setCreateDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>("");

  // project switch: invalidate any in-flight receipt and close the panel so a
  // stale project's receipt can never surface under the new project (FNL2-UI-03)
  useEffect(() => {
    detailReqRef.current += 1;
    setOpenTaskId("");
    setDetail(null);
    setDetailTaskId("");
    setDetailError("");
    setNotice("");
  }, [projectId]);

  const loadDetail = useCallback(
    async (taskId: string) => {
      const gen = ++detailReqRef.current;
      const pid = projectId;
      setDetail(null);
      setDetailError("");
      setDetailTaskId(taskId);
      if (!token || !pid) return;
      const r = await surfaceGetJson(
        token,
        `/api/projects/${encodeURIComponent(pid)}/coding/tasks/${encodeURIComponent(taskId)}`,
      );
      if (gen !== detailReqRef.current) return;
      const t = (r.data?.task ?? r.data) as Record<string, unknown> | null;
      if (!r.ok || !t) {
        setDetail(null);
        setDetailError(
          locale === "hi"
            ? `receipt उपलब्ध नहीं (${r.status || "network"})`
            : `receipt unavailable (${r.status || "network"})`,
        );
        return;
      }
      const planText = t.plan ? JSON.stringify(t.plan, null, 2) : "";
      const evidence = t.evidence;
      const evidenceKeys =
        evidence && typeof evidence === "object" && !Array.isArray(evidence)
          ? Object.keys(evidence as Record<string, unknown>)
          : [];
      const evidenceSummary = evidenceKeys.length
        ? `${evidenceKeys.length}: ${evidenceKeys.slice(0, 6).join(", ")}${evidenceKeys.length > 6 ? "…" : ""}`
        : locale === "hi"
          ? "कोई evidence नहीं"
          : "no evidence";
      setDetail({
        taskId,
        state: String(t.state ?? t.status ?? ""),
        branch: String(t.branch ?? ""),
        workspaceId: String(t.workspaceId ?? ""),
        approvalState: String(t.approvalState ?? ""),
        budgetUsage: t.budgetUsage ? JSON.stringify(t.budgetUsage) : "",
        planText,
        prUrl: String(t.prUrl ?? ""),
        evidenceSummary,
      });
    },
    [token, projectId, locale],
  );

  const toggleDetail = useCallback(
    (taskId: string) => {
      if (openTaskId === taskId) {
        detailReqRef.current += 1;
        setOpenTaskId("");
        setDetail(null);
        setDetailError("");
        return;
      }
      setOpenTaskId(taskId);
      void loadDetail(taskId);
    },
    [openTaskId, loadDetail],
  );

  const createTask = useCallback(async () => {
    if (!token || !projectId || !createDesc.trim()) return;
    setBusy(true);
    setNotice("");
    const r = await fetch(
      `${surfaceApiBase()}/api/projects/${encodeURIComponent(projectId)}/coding/tasks`,
      {
        method: "POST",
        headers: surfaceHeaders(token),
        credentials: "include",
        body: JSON.stringify({ description: createDesc.trim(), mode: "plan_only" }),
      },
    ).catch(() => null);
    setBusy(false);
    if (!r || !r.ok) {
      setNotice(locale === "hi" ? `task निर्माण विफल (${r?.status ?? "network"})` : `task create failed (${r?.status ?? "network"})`);
      return;
    }
    setCreateDesc("");
    setNotice(locale === "hi" ? "plan-mode task बना" : "plan-mode task created");
    tasksQ.retry();
  }, [token, projectId, createDesc, locale, tasksQ]);

  return (
    <SurfacePage
      route="code"
      title={productText("navCode", locale)}
      subtitle={
        locale === "hi"
          ? "कैनॉनिकल coding tasks — plan/diff/approval receipts"
          : "Canonical coding tasks — plan/diff/approval receipts"
      }
    >
      <CodeDesktopHandoffCard />
      {projectsQ.kind === "ready" && projectsQ.data.length > 1 ? (
        <select
          value={projectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          style={{ padding: 6, fontSize: 12, marginBottom: 8, maxWidth: 360 }}
        >
          {projectsQ.data.map((p) => (
            <option key={p.projectId} value={p.projectId}>
              {p.displayName} {p.repository ? `(${p.repository})` : ""}
            </option>
          ))}
        </select>
      ) : null}
      {projectsQ.kind === "ready" && projectId ? (
        <div style={{ ...cardStyle(C), marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
              placeholder={
                locale === "hi"
                  ? "नया coding task (plan mode)…"
                  : "New coding task (plan mode)…"
              }
              style={{ flex: 1, fontSize: 12, padding: 6 }}
            />
            <button disabled={busy || !createDesc.trim()} onClick={() => void createTask()}>
              {locale === "hi" ? "बनाएँ" : "Create"}
            </button>
          </div>
          {notice ? <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{notice}</div> : null}
        </div>
      ) : null}
      {projectsQ.kind === "ready" && !projectId ? (
        <EmptyState />
      ) : projectsQ.kind !== "ready" ? (
        <SurfaceStateView state={projectsQ} onRetry={projectsQ.retry} />
      ) : tasksQ.kind === "ready" ? (
        tasksQ.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {tasksQ.data.map((t) => (
              <div key={t.taskId} style={cardStyle(C)}>
                <div style={{ fontSize: 12, color: C.fg }}>{t.description || t.taskId}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{t.status}</div>
                <button
                  style={{ fontSize: 11, marginTop: 6 }}
                  onClick={() => void toggleDetail(t.taskId)}
                >
                  {openTaskId === t.taskId
                    ? locale === "hi"
                      ? "receipt बंद करें"
                      : "hide receipt"
                    : locale === "hi"
                      ? "receipt"
                      : "receipt"}
                </button>
                {openTaskId === t.taskId ? (
                  detail && detailTaskId === t.taskId ? (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8, whiteSpace: "pre-wrap" }}>
                      {`state: ${detail.state}\nbranch: ${detail.branch}\napproval: ${detail.approvalState}\nbudget: ${detail.budgetUsage}\nevidence: ${detail.evidenceSummary}${detail.prUrl ? `\npr: ${detail.prUrl}` : ""}`}
                      {detail.planText ? (
                        <pre style={{ fontSize: 10, marginTop: 6, maxHeight: 180, overflow: "auto" }}>
                          {detail.planText}
                        </pre>
                      ) : null}
                    </div>
                  ) : detailError && detailTaskId === t.taskId ? (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                      {detailError}{" "}
                      <button style={{ fontSize: 11 }} onClick={() => void loadDetail(t.taskId)}>
                        {locale === "hi" ? "फिर से" : "retry"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>…</div>
                  )
                ) : null}
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={tasksQ} onRetry={tasksQ.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Design ─────────────────────────────────────────────────────────────── */

interface DesignProjectRow {
  projectId: string;
  name: string;
  width: number;
  height: number;
  unit: string;
  version: number;
  layerCount: number;
  updatedAt: number;
}

interface DesignVersionRow {
  version: number;
  savedAt: number;
  savedBy: string;
  comment: string;
}

function mapDesignProject(p: Record<string, unknown>): DesignProjectRow {
  const layers = p.layers;
  return {
    projectId: String(p.projectId ?? ""),
    name: String(p.name ?? p.projectId ?? ""),
    width: Number(p.width ?? 0),
    height: Number(p.height ?? 0),
    unit: String(p.unit ?? ""),
    version: Number(p.version ?? 0),
    layerCount: Array.isArray(layers) ? layers.length : Number(p.layerCount ?? 0),
    updatedAt: Number(p.updatedAt ?? 0),
  };
}

export function DesignSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const { token } = useAuth();
  const projectsQ = useSurfaceQuery<DesignProjectRow[]>("/api/design/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map(
      mapDesignProject,
    ),
  );
  const [openId, setOpenId] = useState("");
  const [versions, setVersions] = useState<DesignVersionRow[] | null>(null);
  const [versionsFor, setVersionsFor] = useState("");
  const [versionsError, setVersionsError] = useState("");
  const versionsReqRef = useRef(0);
  const [newName, setNewName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const callApi = useCallback(
    async (method: string, path: string, body?: unknown) => {
      const r = await fetch(`${surfaceApiBase()}${path}`, {
        method,
        headers: surfaceHeaders(token),
        credentials: "include",
        body: body === undefined ? null : JSON.stringify(body),
      }).catch(() => null);
      const data = (await r?.json().catch(() => null)) as Record<string, unknown> | null;
      return { ok: !!r?.ok, status: r?.status ?? 0, data };
    },
    [token],
  );

  const loadVersions = useCallback(
    async (projectId: string) => {
      const gen = ++versionsReqRef.current;
      setVersions(null);
      setVersionsFor(projectId);
      setVersionsError("");
      const r = await callApi("GET", `/api/design/projects/${encodeURIComponent(projectId)}/versions`);
      if (gen !== versionsReqRef.current) return; // stale reply — a newer open/switch owns the panel
      if (!r.ok || r.data?.ok === false || !r.data) {
        setVersionsError(locale === "hi" ? `versions लोड विफल (${r.status || "network"})` : `versions load failed (${r.status || "network"})`);
        return;
      }
      const list = Array.isArray(r.data?.versions) ? (r.data.versions as Record<string, unknown>[]) : [];
      setVersions(
        list.map((v) => ({
          version: Number(v.version ?? 0),
          savedAt: Number(v.savedAt ?? v.saved_at ?? 0),
          savedBy: String(v.savedBy ?? v.saved_by ?? ""),
          comment: String(v.comment ?? ""),
        })),
      );
    },
    [callApi, locale],
  );

  const toggleVersions = useCallback(
    (projectId: string) => {
      if (openId === projectId) {
        versionsReqRef.current += 1; // drop in-flight reply — panel closed
        setOpenId("");
        return;
      }
      setOpenId(projectId);
      void loadVersions(projectId);
    },
    [openId, loadVersions],
  );

  const createProject = useCallback(async () => {
    if (!newName.trim()) return;
    setBusy(true);
    setNotice("");
    const r = await callApi("POST", "/api/design/projects", { name: newName.trim() });
    setBusy(false);
    setNotice(
      r.ok
        ? locale === "hi" ? "project बना" : "project created"
        : locale === "hi" ? `create विफल (${r.status})` : `create failed (${r.status})`,
    );
    if (r.ok) {
      setNewName("");
      projectsQ.retry();
    }
  }, [newName, callApi, locale, projectsQ]);

  const generateDesign = useCallback(async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    setNotice("");
    const r = await callApi("POST", "/api/design/generate", { prompt: prompt.trim() });
    setBusy(false);
    setNotice(
      r.ok
        ? locale === "hi" ? "AI draft बना — project में जोड़ा गया" : "AI draft created as project"
        : locale === "hi" ? `generate विफल (${r.status})` : `generate failed (${r.status})`,
    );
    if (r.ok) {
      setPrompt("");
      projectsQ.retry();
    }
  }, [prompt, callApi, locale, projectsQ]);

  const saveVersion = useCallback(
    async (projectId: string) => {
      setBusy(true);
      setNotice("");
      const r = await callApi("POST", `/api/design/projects/${encodeURIComponent(projectId)}/save`, {});
      setBusy(false);
      setNotice(
        r.ok
          ? locale === "hi" ? "version saved" : "version saved"
          : locale === "hi" ? `save विफल (${r.status})` : `save failed (${r.status})`,
      );
      if (r.ok && openId === projectId) void loadVersions(projectId); // refresh open panel, never toggle-close
    },
    [callApi, locale, openId, loadVersions],
  );

  return (
    <SurfacePage
      route="design"
      title={productText("navDesign", locale)}
      subtitle={
        locale === "hi"
          ? "कैनॉनिकल design projects — draft/versions/export (publish Website Maker का है)"
          : "Canonical design projects — drafts/versions/export (publish belongs to Website Maker)"
      }
    >
      <div style={{ ...cardStyle(C), marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={locale === "hi" ? "नया design project…" : "New design project…"}
            style={{ fontSize: 12, padding: 6, flex: 1, minWidth: 160 }}
          />
          <button disabled={busy || !newName.trim()} onClick={() => void createProject()}>
            {locale === "hi" ? "बनाएँ" : "Create"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={
              locale === "hi" ? "AI से draft generate करें — जैसे 'sale poster'…" : "Generate AI draft — e.g. 'sale poster'…"
            }
            style={{ fontSize: 12, padding: 6, flex: 1, minWidth: 160 }}
          />
          <button disabled={busy || !prompt.trim()} onClick={() => void generateDesign()}>
            {locale === "hi" ? "Generate" : "Generate"}
          </button>
        </div>
        {notice ? <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{notice}</div> : null}
      </div>
      {projectsQ.kind === "ready" ? (
        projectsQ.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {projectsQ.data.map((p) => (
              <div key={p.projectId} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{p.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  {p.width}×{p.height} {p.unit} · v{p.version} · {p.layerCount} layers
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button style={{ fontSize: 11 }} onClick={() => void toggleVersions(p.projectId)}>
                    {openId === p.projectId
                      ? locale === "hi" ? "versions बंद" : "hide versions"
                      : locale === "hi" ? "versions" : "versions"}
                  </button>
                  <button
                    style={{ fontSize: 11 }}
                    disabled={busy}
                    onClick={() => void saveVersion(p.projectId)}
                  >
                    {locale === "hi" ? "save" : "save"}
                  </button>
                </div>
                {openId === p.projectId ? (
                  versionsError ? (
                    <div style={{ fontSize: 11, color: "#b45309", marginTop: 8 }}>
                      {versionsError}{" "}
                      <button style={{ fontSize: 11 }} onClick={() => void loadVersions(p.projectId)}>
                        {locale === "hi" ? "फिर से" : "retry"}
                      </button>
                    </div>
                  ) : versions === null || versionsFor !== p.projectId ? (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>…</div>
                  ) : versions.length === 0 ? (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                      {locale === "hi" ? "कोई saved version नहीं" : "no saved versions"}
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      {versions.map((v) => (
                        <div key={v.version} style={{ fontSize: 11, color: C.muted }}>
                          v{v.version} · {v.savedBy || "—"} {v.comment ? `· ${v.comment}` : ""}
                        </div>
                      ))}
                    </div>
                  )
                ) : null}
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={projectsQ} onRetry={projectsQ.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Activity ───────────────────────────────────────────────────────────── */

export function ActivitySurface() {
  const { token } = useAuth();
  const { locale } = useLocale();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; detail: string }
    | { kind: "unavailable" }
    | {
        kind: "ready";
        entries: ReturnType<typeof buildActivityLedger>;
        sources: string[];
        failedSources: string[];
      }
  >({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "unavailable" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      const sources: string[] = [];
      const failedSources: string[] = [];
      const assistantTurns: LedgerAssistantTurn[] = [];
      const runs: LedgerCoworkRun[] = [];
      const approvals: LedgerApproval[] = [];
      const usage: LedgerUsageEvent[] = [];

      const convs = await surfaceGetJson(token, "/api/chat/conversations");
      if (convs.ok && convs.data) {
        sources.push("chat.messages");
        const list = Array.isArray(convs.data.conversations)
          ? (convs.data.conversations as Record<string, unknown>[])
          : [];
        // Bounded: the latest 5 conversations' durable messages feed the
        // assistant-turn rows — the ledger never walks the whole store.
        for (const c of list.slice(0, 5)) {
          const convId = String(c.conversationId ?? c.id ?? "");
          if (!convId) continue;
          const msgs = await surfaceGetJson(
            token,
            `/api/chat/conversations/${encodeURIComponent(convId)}/messages`,
          );
          if (msgs.ok && msgs.data) {
            const arr = Array.isArray(msgs.data.messages)
              ? (msgs.data.messages as Record<string, unknown>[])
              : [];
            for (const m of arr) {
              if (String(m.role) !== "assistant") continue;
              const turn = assistantTurnFromMessage(m);
              if (turn) assistantTurns.push(turn);
            }
          }
        }
      } else {
        failedSources.push("chat.messages");
      }

      const runsRes = await surfaceGetJson(token, "/api/cowork/runs");
      if (runsRes.ok && runsRes.data) {
        sources.push("cowork.runs");
        const list = Array.isArray(runsRes.data.runs) ? (runsRes.data.runs as Record<string, unknown>[]) : [];
        for (const r of list) {
          runs.push({
            runId: String(r.runId ?? ""),
            status: String(r.status ?? ""),
            title: typeof r.title === "string" ? r.title : String(r.runId ?? ""),
            updatedAt: typeof r.updatedAt === "number" ? r.updatedAt : undefined,
          });
        }
      } else if (runsRes.status === 401 || runsRes.status === 403) {
        // privileged-only source — honest skip, not an error
      } else {
        failedSources.push("cowork.runs");
      }

      const apprRes = await surfaceGetJson(token, "/api/cowork/approvals");
      if (apprRes.ok && apprRes.data) {
        sources.push("cowork.approvals");
        const list = Array.isArray(apprRes.data.approvals)
          ? (apprRes.data.approvals as Record<string, unknown>[])
          : [];
        for (const a of list) {
          approvals.push({
            approvalId: String(a.approvalId ?? a.id ?? ""),
            status: String(a.status ?? "pending"),
            actionLabel:
              typeof a.actionLabel === "string" ? a.actionLabel : String(a.approvalId ?? a.id ?? ""),
            updatedAt: typeof a.updatedAt === "number" ? a.updatedAt : undefined,
          });
        }
      } else if (apprRes.status !== 401 && apprRes.status !== 403) {
        failedSources.push("cowork.approvals");
      }

      const usageRes = await surfaceGetJson(token, "/api/usage");
      if (usageRes.ok && usageRes.data) {
        sources.push("usage.ledger");
        const list = Array.isArray(usageRes.data.events)
          ? (usageRes.data.events as Record<string, unknown>[])
          : [];
        for (const u of list.slice(0, 100)) {
          usage.push({
            id: String(u.id ?? u.eventId ?? ""),
            label: String(u.kind ?? u.label ?? u.eventType ?? "usage"),
            at: typeof u.at === "number" ? u.at : typeof u.createdAt === "number" ? u.createdAt : 0,
            status: typeof u.status === "string" ? u.status : undefined,
          });
        }
      } else if (usageRes.status !== 401 && usageRes.status !== 403 && usageRes.status !== 503) {
        failedSources.push("usage.ledger");
      }

      if (cancelled) return;
      if (sources.length === 0 && failedSources.length > 0) {
        setState({ kind: "error", detail: failedSources.join(", ") });
        return;
      }
      setState({
        kind: "ready",
        entries: buildActivityLedger({ assistantTurns, runs, approvals, usage }),
        sources,
        failedSources,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <SurfacePage
      route="activity"
      title={productText("navActivity", locale)}
      subtitle={productText("ledgerSource", locale)}
    >
      {state.kind === "ready" && state.failedSources.length > 0 ? (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 0 }}>
          {locale === "hi" ? "कुछ स्रोत लोड नहीं हुए" : "Some sources failed"}: {state.failedSources.join(", ")}
        </p>
      ) : null}
      {state.kind === "ready" ? (
        <ActivityLedgerView locale={locale} entries={state.entries} />
      ) : (
        <SurfaceStateView state={state} />
      )}
    </SurfacePage>
  );
}

/* ── Tasks (deterministic no-LLM operator) ───────────────────────────────── */

interface OperatorProductRow {
  productId: string;
  status: string;
}

export function TasksSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const { token } = useAuth();
  const productsQ = useSurfaceQuery<OperatorProductRow[]>("/api/operator/products", (d) =>
    (Array.isArray(d.products) ? (d.products as Record<string, unknown>[]) : []).map((p) => ({
      productId: String(p.productId ?? ""),
      status: String(p.status ?? p.registrationStatus ?? ""),
    })),
  );
  const [intent, setIntent] = useState("");
  const [fieldsText, setFieldsText] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<{
    kind: "resolved" | "previewed" | "executed" | "error";
    text: string;
    confirmationToken?: string | undefined;
    receipt?: unknown;
  } | null>(null);
  const runGenRef = useRef(0);

  // Any intent/fields edit invalidates the prior stage + confirmation token.
  useEffect(() => {
    runGenRef.current += 1; // drop in-flight replies too
    setStage(null);
    setBusy(false); // invalidated requests release busy — their late replies early-return before touching it
  }, [intent, fieldsText]);

  const callApi = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const r = await fetch(`${surfaceApiBase()}${path}`, {
        method: "POST",
        headers: surfaceHeaders(token),
        credentials: "include",
        body: JSON.stringify(body),
      }).catch(() => null);
      const data = (await r?.json().catch(() => null)) as Record<string, unknown> | null;
      return { ok: !!r?.ok, status: r?.status ?? 0, data };
    },
    [token],
  );

  const fieldsParse = useCallback((): { ok: boolean; fields?: Record<string, unknown> } => {
    const t = fieldsText.trim();
    if (!t) return { ok: true };
    try {
      const v = JSON.parse(t) as unknown;
      return typeof v === "object" && v !== null && !Array.isArray(v)
        ? { ok: true, fields: v as Record<string, unknown> }
        : { ok: false };
    } catch {
      return { ok: false };
    }
  }, [fieldsText]);
  const fieldsInvalid = !fieldsParse().ok;

  const run = useCallback(
    async (kind: "resolve" | "preview" | "execute") => {
      const parsed = fieldsParse();
      if (!parsed.ok) {
        setStage({ kind: "error", text: locale === "hi" ? "अमान्य JSON fields" : "invalid JSON fields" });
        return;
      }
      const gen = ++runGenRef.current;
      setBusy(true);
      const body: Record<string, unknown> = { intentText: intent.trim() };
      if (parsed.fields) body.fields = parsed.fields;
      if (kind === "execute" && stage?.confirmationToken)
        body.confirmationToken = stage.confirmationToken;
      const r = await callApi(`/api/operator/${kind}`, body);
      if (gen !== runGenRef.current) return; // stale reply — inputs changed or newer run in flight
      setBusy(false);
      const d = r.data;
      if (!r.ok || !d || d.ok === false) {
        const code = typeof d?.errorCode === "string" ? d.errorCode : "";
        setStage({
          kind: "error",
          text: `${kind} विफल (${r.status || "network"})${code ? ` — ${code}` : ""}`,
        });
        return;
      }
      const tokenOut = typeof d.confirmationToken === "string" ? d.confirmationToken : undefined;
      if (kind === "resolve") {
        setStage({
          kind: "resolved",
          text: JSON.stringify(d.resolution ?? d, null, 2),
          confirmationToken: tokenOut,
        });
      } else if (kind === "preview") {
        // only a *current successful* preview may mint a confirmationToken — never fall back to a prior stage's
        setStage({
          kind: "previewed",
          text: JSON.stringify(d.preview ?? d, null, 2),
          confirmationToken: tokenOut,
        });
      } else {
        setStage({
          kind: "executed",
          text: JSON.stringify(d.receipt ?? d, null, 2),
          receipt: d.receipt,
        });
      }
    },
    [intent, fieldsParse, callApi, locale, stage],
  );

  return (
    <SurfacePage
      route="tasks"
      title={productText("navTasks", locale)}
      subtitle={
        locale === "hi"
          ? "Deterministic task engine — बिना LLM के resolve→preview→confirm→execute→receipt"
          : "Deterministic task engine — resolve→preview→confirm→execute→receipt without LLM"
      }
    >
      <div style={{ ...cardStyle(C), maxWidth: 720 }}>
        {productsQ.kind === "ready" ? (
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
            {locale === "hi" ? "registered products:" : "registered products:"}{" "}
            {productsQ.data.length === 0
              ? locale === "hi" ? "कोई नहीं" : "none"
              : productsQ.data.map((p) => p.productId).join(", ")}
          </div>
        ) : (
          <SurfaceStateView state={productsQ} onRetry={productsQ.retry} />
        )}
        <input
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder={locale === "hi" ? "क्या करना है — जैसे 'create website draft'…" : "What to do — e.g. 'create website draft'…"}
          style={{ width: "100%", fontSize: 12, padding: 6, boxSizing: "border-box" }}
        />
        <textarea
          value={fieldsText}
          onChange={(e) => setFieldsText(e.target.value)}
          placeholder={locale === "hi" ? 'optional fields JSON — {"name":"..."}' : 'optional fields JSON — {"name":"..."}'}
          rows={2}
          style={{ width: "100%", fontSize: 11, padding: 6, marginTop: 6, boxSizing: "border-box", fontFamily: "monospace" }}
        />
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <button disabled={busy || !intent.trim() || fieldsInvalid} onClick={() => void run("resolve")}>
            {locale === "hi" ? "resolve" : "resolve"}
          </button>
          <button disabled={busy || !intent.trim() || fieldsInvalid} onClick={() => void run("preview")}>
            {locale === "hi" ? "preview" : "preview"}
          </button>
          <button disabled={busy || !stage?.confirmationToken} onClick={() => void run("execute")}>
            {locale === "hi" ? "confirm + execute" : "confirm + execute"}
          </button>
        </div>
        {fieldsInvalid ? (
          <p style={{ fontSize: 11, color: "#b45309", margin: "6px 0 0" }}>
            {locale === "hi" ? "अमान्य JSON — fields ठीक करें" : "invalid JSON — fix fields"}
          </p>
        ) : null}
        {stage ? (
          <pre
            style={{
              fontSize: 11,
              marginTop: 10,
              padding: 8,
              maxHeight: 320,
              overflow: "auto",
              background: C.bg,
              borderRadius: 8,
              whiteSpace: "pre-wrap",
            }}
          >
            {`${stage.kind === "executed" ? (locale === "hi" ? "receipt" : "receipt") : stage.kind}\n${stage.text}`}
          </pre>
        ) : null}
      </div>
    </SurfacePage>
  );
}
